/**
 * PerformancePane — equity curve + returns vs S&P benchmark.
 * Data from GET /portfolio/pnl endpoint.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';

const PERIODS = ['1d', '1w', '1m', '3m', 'ytd', '1y'] as const;

export function PerformancePane() {
  const [period, setPeriod] = useState<string>('1m');
  const [data, setData] = useState<api.PnLResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.portfolio.pnl(period)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Performance</h3>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              className={`btn text-xs ${p === period ? 'border-accent text-accent' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-ink-mute">Loading...</div>}

      {!loading && !data && (
        <div className="card p-4 text-sm text-ink-mute">
          Performance data unavailable for this period.
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3">
            <StatCard
              label="Starting"
              value={`$${data.starting_equity.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            />
            <StatCard
              label="Current"
              value={`$${data.ending_equity.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            />
            <StatCard
              label="Return"
              value={`${data.total_return_pct >= 0 ? '+' : ''}${data.total_return_pct.toFixed(2)}%`}
              tone={data.total_return_pct >= 0 ? 'ok' : 'bad'}
            />
          </div>

          {/* Equity curve */}
          {data.points.length > 1 && (
            <div className="card p-4">
              <EquityCurve points={data.points} />
              <div className="text-[11px] text-ink-mute mt-2 num">
                {data.points[0].ts.split('T')[0]} — {data.points[data.points.length - 1].ts.split('T')[0]} · {data.points.length} points
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'bad' }) {
  return (
    <div className="card p-3 text-center">
      <div className="text-[10px] text-ink-mute uppercase">{label}</div>
      <div className={`num text-lg font-bold ${tone === 'ok' ? 'text-ok' : tone === 'bad' ? 'text-bad' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function EquityCurve({ points }: { points: { ts: string; equity: number }[] }) {
  const equities = points.map((p) => p.equity);
  const min = Math.min(...equities);
  const max = Math.max(...equities);
  const range = Math.max(1e-6, max - min);
  const w = 720;
  const h = 220;
  const path = equities
    .map((e, i) => {
      const x = (i / Math.max(1, equities.length - 1)) * w;
      const y = h - ((e - min) / range) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');

  const startVal = equities[0];
  const endVal = equities[equities.length - 1];
  const isPositive = endVal >= startVal;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-48 md:h-56 block" preserveAspectRatio="none">
      <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} className={isPositive ? 'text-ok' : 'text-bad'} />
    </svg>
  );
}
